/*
  HandwritingSettings — owner-only picker for the vision LLM used to
  transcribe handwritten intake forms and assessment notes.

  Default is OpenAI's gpt-5.6-luna. The dropdown is populated from
  the backend's whitelist so unsupported models can't be saved.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, ScanLine, RotateCcw } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function HandwritingSettings() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-5.6-luna");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/handwriting");
      setCfg(r.data);
      setProvider(r.data.provider);
      setModel(r.data.model);
    } catch (e) {
      toast.error("Couldn't load handwriting settings", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      await api.put("/settings/handwriting", { provider, model });
      toast.success(`Handwriting model set to ${provider} / ${model}`);
      await load();
    } catch (e) {
      toast.error("Couldn't save", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    setSaving(true);
    try {
      await api.delete("/settings/handwriting");
      toast.success("Reverted to default (gpt-5.6-luna)");
      await load();
    } catch (e) {
      toast.error("Couldn't reset", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  const supported = cfg?.supported || {};
  const providerOptions = Object.keys(supported);
  const modelOptions = supported[provider] || [];

  return (
    <Card className="p-4 rounded-lg border border-zinc-200 shadow-none" data-testid="handwriting-settings-card">
      <div className="flex items-center gap-2 mb-3">
        <ScanLine className="h-4 w-4 text-indigo-700" />
        <h3 className="font-medium text-sm">Handwriting recognition</h3>
        {cfg?.is_default && (
          <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200">
            Default
          </span>
        )}
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        Which vision model transcribes handwritten intake forms and assessment notes.
        The Emergent LLM key is used for all providers — no separate key required.
      </p>

      {loading ? (
        <div className="text-sm text-zinc-500"><Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs">Provider</Label>
            <Select value={provider} onValueChange={(v) => { setProvider(v); const list = supported[v] || []; if (list.length) setModel(list[0]); }}>
              <SelectTrigger data-testid="hw-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {providerOptions.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Model</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="hw-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {modelOptions.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={save} disabled={saving} data-testid="hw-settings-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              Save
            </Button>
            <Button variant="outline" onClick={reset} disabled={saving} data-testid="hw-settings-reset">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          </div>
        </div>
      )}
      {cfg?.updated_at && !cfg.is_default && (
        <div className="text-2xs text-zinc-500 mt-3">
          Last changed {new Date(cfg.updated_at).toLocaleString()} by {cfg.updated_by || "—"}
        </div>
      )}
    </Card>
  );
}
